import { Controller } from "@hotwired/stimulus"
import { lockScroll, unlockScroll } from "../utils/scroll_lock"

export default class extends Controller {
  static targets = ["modal", "photoContainer", "currentPhoto", "photoCounter", "prevButton", "nextButton"]
  static values = {
    photos: Array,
    currentIndex: { type: Number, default: 0 }
  }

  connect() {
    // Close modal when clicking outside
    this.boundClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener('click', this.boundClickOutside)

    // Close modal on escape key
    this.boundKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.boundKeydown)

    // Handle arrow key navigation
    this.boundArrowKeys = this.handleArrowKeys.bind(this)
    document.addEventListener('keydown', this.boundArrowKeys)
  }

  disconnect() {
    document.removeEventListener('click', this.boundClickOutside)
    document.removeEventListener('keydown', this.boundKeydown)
    document.removeEventListener('keydown', this.boundArrowKeys)
  }

  openLightbox(event) {
    event.preventDefault()
    const photoIndex = parseInt(event.currentTarget.dataset.photoIndex || 0)
    this.currentIndexValue = photoIndex

    // Debug: Log what we're working with
    console.log('Opening lightbox for photo index:', photoIndex)
    console.log('Photos value:', this.photosValue)
    console.log('Photos value type:', typeof this.photosValue)
    console.log('Photos value length:', this.photosValue ? this.photosValue.length : 'undefined')

    // Ensure photosValue is properly initialized and parsed
    let photos = this.photosValue
    if (typeof photos === 'string') {
      try {
        photos = JSON.parse(photos)
        console.log('Parsed photos from string:', photos)
      } catch (e) {
        console.error('Failed to parse photos JSON:', e)
        return
      }
    }

    if (photos && photos.length > 0) {
      // Store parsed photos for use in other methods
      this._parsedPhotos = photos
      this.openModal()
      this.showPhoto()
    } else {
      console.warn('No photos available to display')
      console.error('Photos data:', photos)
    }
  }

  openModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove('hidden')
      lockScroll()

      // Focus management
      const firstFocusable = this.modalTarget.querySelector('button, [tabindex]:not([tabindex="-1"])')
      if (firstFocusable) {
        firstFocusable.focus()
      }
    }
  }

  closeModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add('hidden')
      unlockScroll()
    }
  }

  showPhoto() {
    // Use parsed photos if available, fallback to photosValue
    const photos = this._parsedPhotos || this.photosValue

    if (this.hasCurrentPhotoTarget && photos && photos.length > 0) {
      const photo = photos[this.currentIndexValue]
      this.currentPhotoTarget.src = photo.url
      this.currentPhotoTarget.alt = photo.filename || 'Installation photo'

      // Update counter
      if (this.hasPhotoCounterTarget) {
        this.photoCounterTarget.textContent = `${this.currentIndexValue + 1} of ${photos.length}`
      }

      // Update navigation buttons
      this.updateNavigationButtons()
    }
  }

  nextPhoto() {
    const photos = this._parsedPhotos || this.photosValue
    if (photos && this.currentIndexValue < photos.length - 1) {
      this.currentIndexValue++
      this.showPhoto()
    }
  }

  prevPhoto() {
    if (this.currentIndexValue > 0) {
      this.currentIndexValue--
      this.showPhoto()
    }
  }

  updateNavigationButtons() {
    const photos = this._parsedPhotos || this.photosValue

    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.disabled = this.currentIndexValue === 0
      this.prevButtonTarget.classList.toggle('opacity-50', this.currentIndexValue === 0)
      this.prevButtonTarget.classList.toggle('cursor-not-allowed', this.currentIndexValue === 0)
    }

    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.disabled = this.currentIndexValue === (photos ? photos.length - 1 : 0)
      this.nextButtonTarget.classList.toggle('opacity-50', this.currentIndexValue === (photos ? photos.length - 1 : 0))
      this.nextButtonTarget.classList.toggle('cursor-not-allowed', this.currentIndexValue === (photos ? photos.length - 1 : 0))
    }
  }

  handleClickOutside(event) {
    // Close modal if clicking on the backdrop
    if (event.target.classList.contains('fixed') && event.target.classList.contains('inset-0')) {
      this.closeModal()
    }
  }

  handleKeydown(event) {
    // Close modal on escape key
    if (event.key === 'Escape' && this.hasModalTarget && !this.modalTarget.classList.contains('hidden')) {
      this.closeModal()
    }
  }

  handleArrowKeys(event) {
    if (!this.hasModalTarget || this.modalTarget.classList.contains('hidden')) {
      return
    }

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault()
        this.prevPhoto()
        break
      case 'ArrowRight':
        event.preventDefault()
        this.nextPhoto()
        break
    }
  }
}
